/**
 * The helplines, as panels that stack.
 *
 * `HelplineDrawer` takes a single `open` boolean, so opening the theory shuts
 * the tips — the wrong shape for a teacher planning a re-teach with the
 * explanation open on one side and the common mistakes beside it. So these
 * stack, cascading each new panel 28px further in with the newest on top.
 *
 * Everything here is TEACHER-facing. The Answer panel prints the answer and its
 * marking scheme, which every student payload strips — the only reason it is
 * safe is that a class report is read after the paper closes. Do not lift this
 * component onto a student surface.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { tenantPath } from '@/lib/tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import { AITutorTopicPanel } from '@/components/home/AITutorTopicPanel';
import type { HelplineBundle, ResourcePicks } from '@cwy/config/contracts';
import { HELPLINE_BLURB, HELPLINE_LABEL, type HelplineKind } from '@/lib/helplines';

export interface OpenPanel {
  /** Stable key, so the same question's TEAM panel is never opened twice. */
  key: string;
  questionId: string;
  qNumber: number;
  kind: HelplineKind;
}

export function panelKey(questionId: string, kind: HelplineKind): string {
  return `${questionId}__${kind}`;
}

/**
 * A student route as a link a child can open from home.
 *
 * Through `tenantPath`, so a school child's link carries `/school/<slug>` —
 * built from the origin alone it opened the root workspace, where a rostered
 * child has no membership and is sent to the workspace chooser.
 */
const studentUrl = (path: string): string => `${window.location.origin}${tenantPath(path)}`;

/**
 * A link that goes HOME, shown to the teacher as what it is.
 *
 * Not a router link: student routes answer "not found" to a teacher — each
 * console is gated to its own role — so "Open the scaffold" in this panel
 * used to land the teacher on a 404. The path is shown instead, and ticking
 * the row puts the full link into Send home.
 */
function StudentLink({ path }: { path: string }) {
  return (
    <span className="block text-fs-10.5" style={{ color: 'var(--ink-3)' }}>
      Student’s link · <code>{path}</code>
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t px-4 py-3" style={{ borderColor: 'var(--border-subtle)' }}>
      <h4
        className="mb-1.5 text-fs-10 font-bold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-3)' }}
      >
        {title}
      </h4>
      {children}
    </section>
  );
}

/** A row a teacher can tick to include in what goes home. */
function Sendable({
  id,
  label,
  picked,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  picked: Set<string>;
  onToggle: (id: string, label: string) => void;
  children?: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 py-1">
      <input
        type="checkbox"
        checked={picked.has(id)}
        onChange={() => onToggle(id, label)}
        className="mt-0.5 h-3.5 w-3.5 flex-none"
      />
      <span className="flex-1 text-fs-12" style={{ color: 'var(--ink-2)' }}>
        {children ?? label}
      </span>
    </label>
  );
}

/**
 * What the teacher has chosen to keep for this sub-topic.
 *
 * Ticking in Send home is a one-off — it composes a message and forgets. This
 * is the other thing the client asked for: a choice that STICKS, so next time
 * anyone opens this topic the two or three that were vetted are already marked.
 */
function usePicks(subtopicId: string) {
  const qc = useQueryClient();
  const { data } = useQuery({
    // The bundle arrives a tick after the panel opens, so without this the
    // first render asks for `/teacher/subtopics//picks`.
    enabled: Boolean(subtopicId),
    queryKey: ['picks', subtopicId],
    queryFn: () => apiGet<ResourcePicks>(`/teacher/subtopics/${subtopicId}/picks`),
  });
  const toggle = useMutation({
    mutationFn: (vars: { kind: 'video' | 'practice'; url: string }) =>
      apiPost(`/teacher/subtopics/${subtopicId}/picks`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['picks', subtopicId] }),
  });
  return {
    videos: new Set(data?.videos ?? []),
    practice: new Set(data?.practice ?? []),
    toggle: (kind: 'video' | 'practice', url: string) => toggle.mutate({ kind, url }),
  };
}

/** Keep / kept — the affordance that makes a pick a pick. */
function PickButton({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="flex-none rounded-full px-2 py-0.5 text-fs-10 font-bold transition"
      style={{
        background: on ? 'var(--brand-lime-soft)' : 'var(--surface-card)',
        border: `1px solid ${on ? 'var(--brand-ink)' : 'var(--border-default)'}`,
        color: on ? 'var(--brand-ink)' : 'var(--ink-3)',
      }}
    >
      {on ? '✓ Picked' : 'Pick'}
    </button>
  );
}

function TeamPanel({
  data,
  picked,
  onToggle,
}: {
  data: HelplineBundle;
  picked: Set<string>;
  onToggle: (id: string, label: string) => void;
}) {
  return (
    <>
      <Section title="Tips">
        {data.tips.map((tip, i) => (
          <Sendable key={tip} id={`tip-${i}`} label={tip} picked={picked} onToggle={onToggle}>
            <span className="font-semibold" style={{ color: 'var(--ink-3)' }}>
              {i === 0 ? 'Light · ' : 'Strong · '}
            </span>
            {tip}
          </Sendable>
        ))}
      </Section>

      <Section title="Explanations">
        <p className="text-fs-12" style={{ color: 'var(--ink-2)' }}>
          {data.theoryRef}
        </p>
        <ol className="mt-1.5 flex flex-col gap-1">
          {data.workedExamples.map((w, i) => (
            <li key={w.step} className="text-fs-12" style={{ color: 'var(--ink-2)' }}>
              <span className="font-bold" style={{ color: 'var(--text-body)' }}>
                {i + 1}. {w.step}
              </span>{' '}
              — {w.explanation}
            </li>
          ))}
        </ol>
        {/* ★ The client was explicit that Orange and Pink need building up from
            the basics, not the same theory page a Green student skims. It is
            always in what goes home, so it is shown here, not ticked. */}
        <p className="mt-2 text-fs-12 font-bold" style={{ color: 'var(--brand-ink)' }}>
          The scaffold for {data.subtopicName.toLowerCase()} goes home with every message
        </p>
        <StudentLink path={data.scaffoldHref} />
      </Section>

      <Section title={`The answer · marked out of ${data.maxMarks}`}>
        <p
          className="rounded-lg p-2 text-fs-12"
          style={{ background: 'var(--paper-sunk)', color: 'var(--text-body)' }}
        >
          {data.correctAnswer}
        </p>
        <ul className="mt-2 flex flex-col gap-1">
          {data.markingScheme.map((m) => (
            <li key={m.step} className="flex items-baseline gap-2 text-fs-12">
              <span
                data-numeric=""
                className="flex-none rounded px-1.5 font-bold"
                style={{ background: 'var(--brand-lime-soft)', color: 'var(--brand-ink)' }}
              >
                {m.marks}
              </span>
              <span className="flex-1" style={{ color: 'var(--ink-2)' }}>
                {m.step}
              </span>
            </li>
          ))}
        </ul>
        {/* No per-step counts: marks are kept per question, with the attempt
            multiplier in them, so a step "lost by" would be a guess. */}
        <p className="mt-1.5 text-fs-10.5" style={{ color: 'var(--ink-3)' }}>
          {data.reached} in this class reached it. Marks are recorded per question, not per step, so
          where each child lost theirs is in their working, not here.
        </p>
        <p className="mt-1.5 text-fs-10.5" style={{ color: 'var(--ink-3)' }}>
          Teacher copy. The answer and its scheme are stripped from anything a student can open.
        </p>
      </Section>

      <Section title="Common mistakes">
        <ul className="flex flex-col gap-1">
          {data.commonErrors.map((e) => (
            <li key={e.description} className="text-fs-12" style={{ color: 'var(--ink-2)' }}>
              {e.description}
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}

function WatchPanel({
  data,
  picked,
  onToggle,
  picks,
}: {
  data: HelplineBundle;
  picked: Set<string>;
  onToggle: (id: string, label: string) => void;
  picks: ReturnType<typeof usePicks>;
}) {
  return (
    <Section title={`${data.videos.length} for ${data.subtopicName.toLowerCase()}`}>
      {data.videos.map((v, i) => (
        <div key={v.url} className="flex items-start gap-2">
          <Sendable id={`video-${i}`} label={v.url} picked={picked} onToggle={onToggle}>
            <a
              href={v.url}
              target="_blank"
              rel="noreferrer"
              className="font-semibold underline-offset-2 hover:underline"
              style={{ color: 'var(--brand-ink)' }}
            >
              {v.title} ↗
            </a>
          </Sendable>
          <span className="mt-1.5">
            <PickButton
              on={picks.videos.has(v.url)}
              onClick={() => picks.toggle('video', v.url)}
              label={`Keep this one for ${data.subtopicName}`}
            />
          </span>
        </div>
      ))}
      <p className="mt-2 text-fs-10.5" style={{ color: 'var(--ink-3)' }}>
        <strong>Pick</strong> keeps one for this sub-topic, so the two or three you have vetted are
        already marked next time anyone in your school opens it. Picked videos are listed first; the
        three at the end are searches, not a checked-over library.
      </p>
    </Section>
  );
}

function PracticePanel({
  data,
  picked,
  onToggle,
  picks,
}: {
  data: HelplineBundle;
  picked: Set<string>;
  onToggle: (id: string, label: string) => void;
  picks: ReturnType<typeof usePicks>;
}) {
  return (
    <Section title="Set as practice or homework">
      {data.practice.map((p, i) => (
        <div key={p.href} className="flex items-start gap-2">
          <Sendable
            id={`practice-${i}`}
            label={`${p.title}: ${studentUrl(p.href)}`}
            picked={picked}
            onToggle={onToggle}
          >
            <span className="font-semibold" style={{ color: 'var(--brand-ink)' }}>
              {p.title}
            </span>
            <span style={{ color: 'var(--ink-3)' }}>
              {' '}
              · {p.kind} · ~{p.estMinutes} min
            </span>
            <StudentLink path={p.href} />
          </Sendable>
          <span className="mt-1.5">
            <PickButton
              on={picks.practice.has(p.href)}
              onClick={() => picks.toggle('practice', p.href)}
              label={`Assign ${p.title} as homework on this sub-topic`}
            />
          </span>
        </div>
      ))}
      <p className="mt-2 text-fs-10.5" style={{ color: 'var(--ink-3)' }}>
        <strong>Pick</strong> assigns one to this sub-topic, so it rides along with the next
        generated homework for the children who are Orange or Pink on it. Ticking the box instead
        just adds it to “Send home” below.
      </p>
    </Section>
  );
}

/**
 * What goes home.
 *
 * It **copies**; it does not deliver. WhatsApp and FCM delivery were
 * deliberately retired from this codebase, so a "Send" that claimed to reach a
 * parent would be the one thing on this sheet that lies. A teacher pastes this
 * into whatever group they already use.
 */
function SendHome({ data, picked }: { data: HelplineBundle; picked: Map<string, string> }) {
  const [copied, setCopied] = useState(false);
  const count = picked.size;

  const message = [
    `${data.topicName} · ${data.subtopicName}`,
    ...[...picked.values()],
    `Start from the basics: ${studentUrl(data.scaffoldHref)}`,
  ].join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is permission-gated; the textarea below is the fallback and
      // is always rendered, so there is nothing to recover from here.
      setCopied(false);
    }
  };

  return (
    <div
      className="border-t px-4 py-3"
      style={{ borderColor: 'var(--border-strong)', background: 'var(--paper-sunk)' }}
    >
      <div className="flex items-center justify-between gap-2">
        <h4
          className="text-fs-11 font-bold uppercase tracking-wide"
          style={{ color: 'var(--ink-2)' }}
        >
          Send home
        </h4>
        <button
          type="button"
          onClick={copy}
          disabled={count === 0}
          className="rounded-full px-3 py-1 text-fs-11 font-bold transition disabled:opacity-40"
          style={{ background: 'var(--brand-ink)', color: 'var(--text-on-forest)' }}
        >
          {copied ? 'Copied' : `Copy ${count || ''}`.trim()}
        </button>
      </div>
      <p className="mt-1 text-fs-10.5" style={{ color: 'var(--ink-3)' }}>
        {count === 0
          ? 'Tick one or two things above.'
          : 'Copies the text below — paste it into your class group. Nothing is sent from here.'}
      </p>
      {count > 0 && (
        <textarea
          readOnly
          value={message}
          rows={Math.min(6, count + 2)}
          className="mt-2 w-full rounded-lg p-2 text-fs-11"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            color: 'var(--ink-2)',
          }}
        />
      )}
    </div>
  );
}

function Panel({
  panel,
  paperId,
  classroomId,
  index,
  onClose,
}: {
  panel: OpenPanel;
  paperId: string;
  classroomId: string;
  index: number;
  onClose: (key: string) => void;
}) {
  // A Map, not a Set: the value is the line that goes into the copied message,
  // so ticking a video carries its URL and ticking a tip carries its words.
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  const toggle = (id: string, label: string) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, label);
      return next;
    });

  // Keyed on the paper and class too: the step and mistake counts are that
  // class's, on that paper.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['helplines', panel.questionId, paperId, classroomId],
    queryFn: () =>
      apiGet<HelplineBundle>(
        `/teacher/questions/${panel.questionId}/helplines?paperId=${encodeURIComponent(paperId)}&classroomId=${encodeURIComponent(classroomId)}`,
      ),
  });

  const keys = new Set(picked.keys());
  // Safe before the bundle loads: the hook keys on the id, and an empty
  // sub-topic id simply returns nothing to show as picked.
  const picks = usePicks(data?.subtopicId ?? '');

  return (
    <motion.aside
      className="pointer-events-auto fixed top-0 z-50 flex h-full w-[min(92vw,26rem)] flex-col overflow-y-auto shadow-xl"
      // Each panel sits a little left of the one beneath it, so the stack reads
      // as a stack rather than one panel replacing another.
      style={{
        right: 0,
        marginRight: index * 28,
        background: 'var(--surface-card)',
        borderLeft: '1.5px solid var(--border-strong)',
      }}
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.18 }}
      role="dialog"
      aria-label={`${HELPLINE_LABEL[panel.kind]} — question ${panel.qNumber}`}
    >
      <header
        className="sticky top-0 z-10 flex items-start justify-between gap-2 px-4 py-3"
        style={{
          borderBottom: '1.5px solid var(--border-strong)',
          background: 'var(--surface-card)',
        }}
      >
        <div>
          <p className="text-sm font-bold" style={{ color: 'var(--text-strong)' }}>
            {HELPLINE_LABEL[panel.kind]}{' '}
            <span style={{ color: 'var(--ink-3)' }}>· Q{panel.qNumber}</span>
          </p>
          <p className="text-fs-11" style={{ color: 'var(--ink-3)' }}>
            {HELPLINE_BLURB[panel.kind]}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onClose(panel.key)}
          aria-label={`Close ${HELPLINE_LABEL[panel.kind]}`}
          className="flex-none text-lg leading-none"
          style={{ color: 'var(--ink-3)' }}
        >
          ✕
        </button>
      </header>

      {isLoading && (
        <div
          className="m-4 h-40 animate-pulse rounded-xl"
          style={{ background: 'var(--surface-sunk)' }}
        />
      )}
      {isError && (
        <p className="p-4 text-fs-12" style={{ color: 'var(--ink-3)' }}>
          Could not load the helplines for this question.
        </p>
      )}

      {data && (
        <>
          <div className="flex-1">
            {panel.kind === 'TEAM' && <TeamPanel data={data} picked={keys} onToggle={toggle} />}
            {panel.kind === 'WATCH' && (
              <WatchPanel data={data} picked={keys} onToggle={toggle} picks={picks} />
            )}
            {panel.kind === 'PRACTICE' && (
              <PracticePanel data={data} picked={keys} onToggle={toggle} picks={picks} />
            )}
            {panel.kind === 'TUTOR' && (
              <div className="px-4 py-3">
                <AITutorTopicPanel subtopicId={data.subtopicId} topicLabel={data.subtopicName} />
              </div>
            )}
          </div>
          {panel.kind !== 'TUTOR' && <SendHome data={data} picked={picked} />}
        </>
      )}
    </motion.aside>
  );
}

export function HelplineStack({
  paperId,
  classroomId,
  panels,
  onClose,
  onCloseAll,
}: {
  /** The paper and class the report is reading — whose counts these are. */
  paperId: string;
  classroomId: string;
  panels: OpenPanel[];
  onClose: (key: string) => void;
  onCloseAll: () => void;
}) {
  return (
    <AnimatePresence>
      {panels.length > 0 && (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-40 bg-black/25 print:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onCloseAll}
          />
          {/* Oldest first, so a newly opened panel lands on top of the pile. */}
          {panels.map((panel, i) => (
            <Panel
              key={panel.key}
              panel={panel}
              paperId={paperId}
              classroomId={classroomId}
              index={panels.length - 1 - i}
              onClose={onClose}
            />
          ))}
        </>
      )}
    </AnimatePresence>
  );
}
